import React from 'react';
import { Box, Flex, Text, useColorModeValue, keyframes } from '@chakra-ui/react';

// Define the spinner animation
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

/**
 * Professional Loading Spinner Component
 * @param {Object} props
 * @param {string} props.message - Loading message to display (optional)
 * @param {string} props.size - Spinner size: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} props.fullHeight - If true, takes full viewport height (default: false)
 * @returns {JSX.Element}
 */
export default function LoadingSpinner({ 
  message = 'Chargement...', 
  size = 'md',
  fullHeight = false 
}) {
  const bgColor = useColorModeValue('gray.50', 'navy.900');
  const spinnerColor = '#006133'; // Your theme primary color
  const accentColor = '#B62B30'; // Your theme accent color
  
  const spinnerSizes = {
    sm: { outer: '40px', inner: '35px', borderWidth: '3px' },
    md: { outer: '60px', inner: '55px', borderWidth: '4px' },
    lg: { outer: '80px', inner: '75px', borderWidth: '5px' },
  };
  
  const currentSize = spinnerSizes[size] || spinnerSizes.md;

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      h={fullHeight ? '100%' : 'auto'}
      minH={fullHeight ? '600px' : '200px'}
      bg={fullHeight ? 'transparent' : 'transparent'}
      p="40px"
      gap="20px"
      width="100%">
      
      {/* Outer spinning ring */}
      <Box
        w={currentSize.outer}
        h={currentSize.outer}
        borderRadius="50%"
        border={`${currentSize.borderWidth} solid`}
        borderColor={`${spinnerColor}20`}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center">
        
        {/* Inner spinning ring */}
        <Box
          w={currentSize.inner}
          h={currentSize.inner}
          borderRadius="50%"
          border={`${currentSize.borderWidth} solid`}
          borderTop={`${currentSize.borderWidth} solid ${spinnerColor}`}
          borderRight={`${currentSize.borderWidth} solid ${accentColor}`}
          borderBottom={`${currentSize.borderWidth} solid transparent`}
          borderLeft={`${currentSize.borderWidth} solid transparent`}
          animation={`${spin} 1.5s linear infinite`}
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center">
          
          {/* Center dot */}
          <Box
            w="8px"
            h="8px"
            borderRadius="50%"
            bg={spinnerColor}
            animation={`${pulse} 2s ease-in-out infinite`}
          />
        </Box>
      </Box>

      {/* Loading message */}
      {message && (
        <Text
          fontSize={size === 'sm' ? '12px' : size === 'lg' ? '16px' : '14px'}
          fontWeight="500"
          color="#455879"
          fontFamily="Poppins, sans-serif"
          mt="10px">
          {message}
        </Text>
      )}

      {/* Animated dots */}
      <Box display="flex" gap="4px" mt="10px">
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            w="6px"
            h="6px"
            borderRadius="50%"
            bg={spinnerColor}
            animation={`${pulse} 1.4s ease-in-out infinite`}
            animationDelay={`${i * 0.2}s`}
          />
        ))}
      </Box>
    </Flex>
  );
}
